import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ReactQueryKeys } from '../../../common/react-query-keys';
import {
  hasCompletedCvShare,
  shouldRefreshApplicationDetails,
  withPreservedCandidateReply,
} from '../candidateReplySync';
import { useChannelListState } from '../ChannelList/hooks/useChannelListState';
import { GlintsChatWSEvent, WSEventOperation } from '../client/ws-event';
import { transformChannelResponseToData } from '../helpers';
import { useGlintsChatClientContext } from '../provider/GlintsChatClientProvider';

export default function useChannelUpdateEventListener() {
  const client = useGlintsChatClientContext();
  const queryClient = useQueryClient();
  const { updateChannelById } = useChannelListState();

  useEffect(() => {
    const handleEvent = (event: GlintsChatWSEvent) => {
      if (event.operation !== WSEventOperation.UPDATE_CHANNEL) return;

      const { opData: updatedChannel } = event;

      updateChannelById(
        updatedChannel.id,
        channel => {
          const isStatusChanged =
            channel.applicationStatus !== updatedChannel.applicationStatus;
          const isCandidateReplyCompleted =
            !hasCompletedCvShare(channel) &&
            hasCompletedCvShare(updatedChannel);

          // For RT/CV Finder Application, if they reply to emp chat, it changes the
          // application status to IN_REVIEW, we need to refetch the application details
          // to access the hidden information, such as Email and Whatsapp Number.
          // A completed CV share records the first candidate reply without changing
          // the application status, so it needs the same refetch.
          if (
            isStatusChanged ||
            (isCandidateReplyCompleted &&
              shouldRefreshApplicationDetails(queryClient, updatedChannel))
          ) {
            queryClient.invalidateQueries({
              queryKey: ReactQueryKeys.applicationDetails(channel.id),
            });
          }

          return withPreservedCandidateReply(
            transformChannelResponseToData({
              ...updatedChannel,
              messageList: channel.messageList,
            }),
            channel
          );
        },
        'replace'
      );
    };

    client.on(WSEventOperation.UPDATE_CHANNEL, handleEvent);

    return () => {
      client.off(WSEventOperation.UPDATE_CHANNEL, handleEvent);
    };
  }, [client, queryClient, updateChannelById]);
}
